import React from 'react';

export type ErrorBoundaryProps = {
  children: React.ReactNode;
  /**
   * Rendered in place of the subtree that threw. Defaults to nothing, so a
   * failed section simply disappears instead of taking the page down with it.
   */
  fallback?: React.ReactNode;
  /** Names the guarded subtree in the console report. */
  name?: string;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Stops one broken subtree from blanking a whole react-rails island.
 *
 * React 18 unmounts the entire root when a render error reaches it, so without
 * a boundary a single bad prop takes down everything mounted in that root —
 * navigation included.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { name } = this.props;
    // This app has no error-reporting SDK, so the console is the only channel —
    // and it is the one the browser smoke suite's canary watches. Swallowing the
    // failure silently would trade a visible outage for an invisible one.
    console.error(`[ErrorBoundary${name ? `: ${name}` : ''}]`, error, errorInfo.componentStack);
  }

  render() {
    const { children, fallback } = this.props;

    if (this.state.hasError) {
      return fallback ?? null;
    }

    return children;
  }
}

export default ErrorBoundary;
